import { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Spin } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { ArrowRightOutlined, LoadingOutlined, SwapOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { checkTableName, type TableReservationNotice } from "../../../../../api/bills";
import { reservationToConfirm } from "./tableReservationNotice";
import { useReservationConfirm } from "./useReservationConfirm";
import { isCloudUnreachableError } from "../../../../../api/client";
import { useRenameBill } from "../../../../../hooks/useBills";
import { useViewport } from "../../../../../hooks/useViewport";
import {
  ModalHeader,
  GhostButton,
  PrimaryButton,
  footerButtonHeight,
} from "../../modalPrimitives";
import { NewTableKeypad } from "./NewTableKeypad";
import { TableCheckLine } from "./TableCheckLine";
import { tableTakenText } from "./tableTakenText";

interface RenameTableModalProps {
  open: boolean;
  billId: number | null;
  currentName: string;
  onClose: () => void;
}

const MAX_NAME_LENGTH = 50;

/**
 * „Смяна на маса" — сметката се мести на друг номер: в полето „12 → 5"
 * (откъде → накъде), под него — фин ред с проверката (заета — червено и от
 * кого, без сумата), цифрите са тези на „Нова маса" (тире вместо „изчисти").
 * Новият номер се пише отначало; заетото име спира „Премести", а
 * непровереното (без връзка) — не.
 */
export const RenameTableModal = ({
  open,
  billId,
  currentName,
  onClose,
}: RenameTableModalProps) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const renameBillMutation = useRenameBill();
  const [tableName, setTableName] = useState("");
  const [error, setError] = useState("");
  // Проверката за свободно име е пропаднала. ОТДЕЛНО от `error`, защото
  // `error` заключва „Запази" — а непроверено име НЕ Е основание да се
  // откаже преименуването (заетото име се отказва от самото преименуване).
  const [checkWarning, setCheckWarning] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  // Резервацията на проверения номер (от същата проверка като „заета") —
  // при „Запази" се пита „продължаваш ли?". Пази и името, за което е.
  const [reservationCheck, setReservationCheck] = useState<{
    name: string;
    notice: TableReservationNotice;
  } | null>(null);
  const { confirmNotice, reservationConfirmModal } = useReservationConfirm();
  const submitRef = useRef<() => void>(() => {});
  const cancelRef = useRef<() => void>(() => {});
  const checkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // При всяко отваряне новият номер е празен — текущият стои вляво от стрелката.
  useEffect(() => {
    if (open) {
      setTableName("");
      setError("");
      setCheckWarning("");
      setIsChecking(false);
      setReservationCheck(null);
    }
  }, [open, currentName]);

  const checkTableNameWithDebounce = useCallback(
    (name: string) => {
      if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current);

      const trimmed = name.trim();
      setCheckWarning("");
      setReservationCheck(null);
      if (!trimmed || trimmed.toLowerCase() === currentName.trim().toLowerCase()) {
        setIsChecking(false);
        return;
      }

      setIsChecking(true);
      checkTimeoutRef.current = setTimeout(async () => {
        try {
          // Със сметката: при лична номерация се гледат масите на собственика ѝ.
          const check = await checkTableName(trimmed, billId ?? undefined);
          if (check.exists) {
            // От кого е заета — без сумата (BE `takenBy`; касата и по-стар облак го нямат).
            setError(tableTakenText(t, trimmed, check.takenBy));
          } else {
            setError("");
          }
          const notice = reservationToConfirm(check);
          setReservationCheck(notice ? { name: trimmed, notice } : null);
        } catch (err) {
          // Дотук тук стоеше само `console.error` и екранът МЪЛЧЕШЕ: при
          // спрял интернет въртележката се въртеше, после спираше и нищо не
          // се променяше — операторът не знаеше проверено ли е името, или не.
          // Сега го пише; „Запази" остава активно нарочно (инвариант
          // „при съмнение обслужвай"): заето име се отказва от самото
          // преименуване, а непроверено име не е причина да не се опита.
          setCheckWarning(
            isCloudUnreachableError(err)
              ? t("ordering.renameCheckOffline")
              : t("ordering.renameCheckFailed", {
                  reason: getApiErrorMessage(err, t("common.error")),
                }),
          );
        } finally {
          setIsChecking(false);
        }
      }, 500);
    },
    [t, currentName, billId],
  );

  const applyChange = useCallback(
    (next: string) => {
      const clipped = next.slice(0, MAX_NAME_LENGTH);
      setTableName(clipped);
      if (error) setError("");
      if (clipped) {
        checkTableNameWithDebounce(clipped);
      } else {
        setIsChecking(false);
        setCheckWarning("");
        if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current);
      }
    },
    [error, checkTableNameWithDebounce],
  );

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        submitRef.current();
      } else if (e.key === "Escape") {
        // preventDefault = „Esc е консумиран" за глобалната Esc-навигация
        e.preventDefault();
        cancelRef.current();
      } else if (
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        /^[\p{L}\p{N} .-]$/u.test(e.key)
      ) {
        setTableName((prev) => {
          const next = (prev + e.key).slice(0, MAX_NAME_LENGTH);
          if (error) setError("");
          checkTableNameWithDebounce(next);
          return next;
        });
      } else if (e.key === "Backspace") {
        setTableName((prev) => {
          const next = prev.slice(0, -1);
          if (next) {
            checkTableNameWithDebounce(next);
          } else {
            setError("");
            setCheckWarning("");
            setIsChecking(false);
            if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current);
          }
          return next;
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, error, checkTableNameWithDebounce]);

  const handleCharacter = (character: string) => applyChange(tableName + character);
  const handleBackspace = () => applyChange(tableName.slice(0, -1));

  submitRef.current = async () => {
    if (!billId) return;
    const trimmed = tableName.trim();
    if (!trimmed) {
      setError(t("ordering.renameTableSubtitle"));
      return;
    }
    if (isChecking || error) return;
    if (trimmed === currentName.trim()) {
      onClose();
      return;
    }
    // Новият номер е зает/резервиран от резервация → „Продължаваш ли?"
    if (
      reservationCheck?.name === trimmed &&
      !(await confirmNotice(trimmed, reservationCheck.notice))
    ) {
      return;
    }
    renameBillMutation.mutate(
      { billId, name: trimmed },
      {
        onSuccess: () => {
          message.success(
            t("ordering.renameTableSuccess", { name: trimmed }),
          );
          onClose();
        },
        onError: (err: unknown) => {
          // Локализирано съобщение в самата форма — не суровият detail
          setError(getApiErrorMessage(err, t("ordering.renameTableError")));
        },
      },
    );
  };

  cancelRef.current = () => {
    onClose();
  };

  const width = isMobile ? undefined : isTablet ? 420 : 460;
  const footerHeight = isNarrow ? footerButtonHeight(true) : 56;
  const isSaving = renameBillMutation.isPending;
  const hasTarget = tableName.length > 0;

  return (
    <>
    <Modal
      open={open}
      onCancel={cancelRef.current}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="bg-pos-card font-sans text-pos-ink">
        <ModalHeader
          icon={<SwapOutlined />}
          title={t("ordering.renameTable")}
          subtitle={t("ordering.moveTableSubtitle", { name: currentName })}
          onClose={cancelRef.current}
          isNarrow={isNarrow}
        />

        <div className={`flex flex-col ${isNarrow ? "gap-3.5 p-4" : "gap-4 px-6 pt-5 pb-6"}`}>
          {/* Откъде → накъде: текущата маса сиво, новият номер в акцента. */}
          <div
            data-testid="move-table-slot"
            className={`relative flex items-center justify-center gap-4 rounded-[12px] border px-11 font-pos-mono font-bold leading-none transition-colors duration-[160ms] ${
              isNarrow ? "h-[52px]" : "h-[60px]"
            } ${
              error ? "border-pos-danger-ink" : hasTarget ? "border-pos-accent" : "border-pos-line2"
            } ${hasTarget ? "bg-pos-selected-bg" : "bg-pos-canvas"}`}
          >
            <span className="max-w-[40%] truncate text-[22px] text-pos-soft2">{currentName}</span>
            <ArrowRightOutlined className="shrink-0 text-[14px] text-pos-muted" />
            <span
              className={`max-w-[40%] truncate tracking-[1px] ${
                hasTarget ? "text-[28px] text-pos-accent-ink" : "text-[22px] text-pos-muted"
              }`}
            >
              {tableName || "—"}
            </span>
            {isChecking && (
              <span className="absolute top-1/2 right-3.5 -translate-y-1/2">
                <Spin indicator={<LoadingOutlined spin className="text-[18px] text-pos-accent" />} />
              </span>
            )}
          </div>

          {/* Проверката — фин ред под полето (общ с „Нова маса"): заета/грешка в
              червено, непроверено (без връзка) — в акцента, защото не спира „Премести". */}
          {error ? (
            <TableCheckLine tone="error">{error}</TableCheckLine>
          ) : checkWarning ? (
            <TableCheckLine tone="warning">{checkWarning}</TableCheckLine>
          ) : null}

          <NewTableKeypad
            onCharacter={handleCharacter}
            onBackspace={handleBackspace}
            backspaceDisabled={tableName.length === 0}
            isNarrow={isNarrow}
          />

          <div className="mt-1 grid grid-cols-[1fr_3fr] gap-2.5">
            <GhostButton
              label={t("common.back")}
              onClick={cancelRef.current}
              shortcut={isDesktop ? "Esc" : undefined}
              height={footerHeight}
            />
            <PrimaryButton
              label={t("ordering.moveTableButton")}
              onClick={submitRef.current}
              disabled={
                !tableName.trim() ||
                isChecking ||
                !!error ||
                tableName.trim() === currentName.trim()
              }
              loading={isSaving}
              shortcut={isDesktop ? "⏎" : undefined}
              height={footerHeight}
            />
          </div>
        </div>
      </div>
    </Modal>
    {reservationConfirmModal}
    </>
  );
};
